/**
 * The companion roster. Pure data + SVG geometry - no DOM, no framework.
 * Adding a sport means adding one entry here; every screen picks it up.
 */

export const COMPANIONS = [
  {
    id: 'basketball', name: 'Buddy', sport: 'Basketball', emoji: '\u{1F3C0}',
    studies: "Shooting form across the top guards, wings and bigs: elbow alignment, one-motion release, footwork before the catch, follow-through hold, free throw routine, fatigue patterns late in sessions.",
    title: 'The Original',
    story: "Born on a cracked outdoor court and never left. Buddy has seen every bad habit a jump shot can have and still believes yours is one rep from clean.",
    image: '/companions-3d-basketball.png', // 3D render; others fall back to SVG
    base: '#ff7a2f', shade: '#d9501a', line: '#3a1c08',
    blurb: 'Hype, loud, lives for the fourth quarter.',
    seams: 'M50 6 V94 M6 50 H94 M18 18 Q50 50 18 82 M82 18 Q50 50 82 82',
    tracks: ['elbow', 'release', 'follow'],
  },
  {
    id: 'soccer', name: 'Striker', sport: 'Soccer', emoji: '\u{26BD}',
    studies: "Striking technique from the top forwards and midfielders: plant foot, instep drive, placement over power, first touch into the shot, finishing under pressure.",
    image: '/companions-3d-soccer.png',
    title: 'The Tactician',
    story: "Striker reads the whole pitch before the ball moves. Calm voice, sharp eye, and a soft spot for anyone who practices when nobody is watching.",
    base: '#f7f9fc', shade: '#c9d3e0', line: '#16202e',
    blurb: 'Calm, technical, sees the whole pitch.',
    seams: 'M50 20 L68 33 L61 55 L39 55 L32 33 Z M50 20 V6 M68 33 L84 25 M61 55 L70 76 M39 55 L30 76 M32 33 L16 25',
    tracks: ['plant', 'strike', 'follow'],
  },
  {
    id: 'baseball', name: 'Ace', sport: 'Baseball', emoji: '\u{26BE}',
    studies: "Hitting mechanics from the top hitters at every spot in the order: load, stride, swing path, head stillness, pitch recognition, on-deck routine.",
    image: '/companions-3d-baseball.png',
    title: 'The Patient One',
    story: "Ace has watched ten thousand pitches and rushes nothing. Every rep is a pitch count, every fix is one adjustment at a time.",
    base: '#fdfdfd', shade: '#dfe5ee', line: '#c8102e',
    blurb: 'Patient, precise, reads every pitch.',
    seams: 'M26 12 Q42 50 26 88 M74 12 Q58 50 74 88',
    tracks: ['stance', 'swing', 'follow'],
  },
  {
    id: 'football', name: 'Blitz', sport: 'Football', emoji: '\u{1F3C8}',
    studies: "Throwing and catching mechanics from the top quarterbacks and receivers: base, hip rotation, release point, route footwork, hands at the catch.",
    image: '/companions-3d-football.png',
    title: 'The Motor',
    story: "Blitz does not know what an off day is. Relentless, loud, zero chill, and the first one to celebrate when your effort finally shows in the numbers.",
    base: '#8b4a22', shade: '#5f2f12', line: '#fdfdfd',
    blurb: 'Relentless. Zero chill, all effort.',
    seams: 'M50 22 V78 M38 34 H62 M38 46 H62 M38 58 H62 M38 70 H62',
    tracks: ['grip', 'release', 'spiral'],
  },
  {
    id: 'hockey', name: 'Slapshot', sport: 'Hockey', emoji: '\u{1F3D2}',
    studies: "Shooting from the top forwards and defensemen: weight transfer, stick flex, wrist and slap shot release, quick release in traffic.",
    image: '/companions-3d-hockey.png',
    title: 'The Straight Shooter',
    story: "Slapshot says it once, says it plain, and gets back to work. Blunt because it cares, fast because your session is short.",
    base: '#2a2f38', shade: '#14181f', line: '#7c879a',
    blurb: 'Blunt, fast, zero wasted words.',
    seams: 'M22 50 H78 M22 40 H78 M22 60 H78',
    tracks: ['load', 'flex', 'follow'],
  },
  {
    id: 'rugby', name: 'Scrum', sport: 'Rugby', emoji: '\u{1F3C9}',
    studies: "Passing and kicking from the top backs and forwards: spiral pass, contact posture, kicking approach, catch under pressure.",
    image: '/companions-3d-rugby.png',
    title: 'The Honest One',
    story: "Scrum never sugarcoats a rep. Tough, fair, and the companion you want when you are ready to hear the truth about your form.",
    base: '#f0f3f8', shade: '#cfd8e4', line: '#1d3b2a',
    blurb: 'Tough, honest, never sugarcoats it.',
    seams: 'M50 20 V80 M34 38 H66 M34 62 H66',
    tracks: ['grip', 'pass', 'follow'],
  },
  {
    id: 'cricket', name: 'Wicket', sport: 'Cricket', emoji: '\u{1F3CF}',
    studies: "Batting technique from the top order down: stance, backlift, front foot drive, head position, shot selection by delivery.",
    image: '/companions-3d-cricket.png',
    title: 'The Strategist',
    story: "Wicket thinks in overs, not moments. Sharp, witty, endlessly patient, and always three shots ahead of where you are.",
    base: '#b3001b', shade: '#7d0012', line: '#f5e6c8',
    blurb: 'Sharp, witty, endlessly patient.',
    seams: 'M18 50 H82 M22 44 H78 M22 56 H78',
    tracks: ['stance', 'swing', 'follow'],
  },
  {
    id: 'tennis', name: 'Volley', sport: 'Tennis', emoji: '\u{1F3BE}',
    studies: "Serve and groundstrokes from the top players: toss consistency, trophy position, racket drop, forehand contact point, footwork recovery.",
    image: '/companions-3d-tennis.png',
    title: 'The Competitor',
    story: "Volley turns every drill into a match. Quick, chatty, competitive about everything, and secretly keeping score of your improvement.",
    base: '#d4f53d', shade: '#a8c723', line: '#f7f9fc',
    blurb: 'Quick, chatty, competitive as anything.',
    seams: 'M14 26 Q50 50 14 74 M86 26 Q50 50 86 74',
    tracks: ['setup', 'contact', 'follow'],
  },
  {
    id: 'volleyball', name: 'Spike', sport: 'Volleyball', emoji: '\u{1F3D0}',
    studies: "Attacking from the top outside hitters and middles: approach timing, arm swing, contact point, blocking hands, serve receive posture.",
    image: '/companions-3d-volleyball.png',
    title: 'The Teammate',
    story: "Spike is bouncy, positive, and all about the assist. Big on teamwork, bigger on making sure you leave every session better than you arrived.",
    base: '#fdfdfd', shade: '#d3dceb', line: '#1f6fd0',
    blurb: 'Bouncy, positive, big on teamwork.',
    seams: 'M20 24 Q50 50 20 76 M50 6 Q62 50 50 94 M80 24 Q50 50 80 76',
    tracks: ['approach', 'contact', 'follow'],
  },
  {
    id: 'pickleball', name: 'Dink', sport: 'Pickleball', emoji: '\u{1F3BE}', comingSoon: true,
    image: '/companions-3d-pickleball.png',
    title: 'The Newcomer',
    story: "Dink showed up to the courts last summer and never left. Friendly, quick, and convinced the kitchen line is the most exciting place in sports.",
    studies: "Dinks, third shot drops and serves from the top pros: paddle angle, soft hands at the kitchen, footwork on the transition, court positioning in doubles.",
    base: '#ffd23f', shade: '#d9a800', line: '#6b5300',
    blurb: 'Friendly, quick, lives at the kitchen line.',
    seams: 'M35 30 a3 3 0 1 0 0.1 0 M50 26 a3 3 0 1 0 0.1 0 M65 30 a3 3 0 1 0 0.1 0 M30 50 a3 3 0 1 0 0.1 0 M70 50 a3 3 0 1 0 0.1 0 M35 70 a3 3 0 1 0 0.1 0 M50 74 a3 3 0 1 0 0.1 0 M65 70 a3 3 0 1 0 0.1 0',
    tracks: ['paddle', 'touch', 'feet'],
  },
  {
    id: 'golf', name: 'Birdie', sport: 'Golf', emoji: '\u{26F3}', comingSoon: true,
    image: '/companions-3d-golf.png',
    title: 'The Perfectionist',
    story: "Birdie has watched ten thousand swings and still gets excited about a good tempo. Quiet, precise, and generous with the one fix that matters.",
    studies: "Swing mechanics from the top tour players: grip, posture, takeaway, tempo, hip rotation, low point control, putting stroke and pre-shot routine.",
    base: '#ffffff', shade: '#d7dde6', line: '#4b5565',
    blurb: 'Quiet, precise, obsessed with tempo.',
    seams: 'M30 34 a2.5 2.5 0 1 0 0.1 0 M44 30 a2.5 2.5 0 1 0 0.1 0 M58 30 a2.5 2.5 0 1 0 0.1 0 M72 34 a2.5 2.5 0 1 0 0.1 0 M26 52 a2.5 2.5 0 1 0 0.1 0 M76 52 a2.5 2.5 0 1 0 0.1 0 M30 70 a2.5 2.5 0 1 0 0.1 0 M72 70 a2.5 2.5 0 1 0 0.1 0',
    tracks: ['grip', 'tempo', 'rotation'],
  },
  {
    id: 'hyrox', name: 'Grit', sport: 'Hyrox', emoji: '\u{1F3CB}\u{FE0F}', comingSoon: true,
    image: '/companions-3d-hyrox.png',
    title: 'The Engine',
    story: "Grit is a wall ball that has been thrown a million times and asked for more. Relentless, warm, and the voice in your ear on the last sled push.",
    studies: "Race pacing and station technique from the top Hyrox athletes: run splits, sled push and pull posture, wall ball rhythm, rowing and ski erg stroke, burpee broad jump efficiency, transitions.",
    base: '#2b2f36', shade: '#15181d', line: '#ff5a1f',
    blurb: 'Relentless, warm, built for the last rep.',
    seams: 'M20 50 H80 M50 20 V80',
    tracks: ['pace', 'posture', 'rhythm'],
  },
  {
    id: 'pingpong', name: 'Ping', sport: 'Ping Pong', emoji: '\u{1F3D3}', comingSoon: true,
    image: '/companions-3d-pingpong.png',
    title: 'The Quick One',
    story: "Ping talks fast, thinks faster, and celebrates every rally like a point in the finals. Light, cheerful, allergic to slow feet.",
    studies: "Strokes and footwork from the top table tennis players: ready position, forehand and backhand loop, serve spin variation, third ball attack, recovery steps.",
    base: '#fff7e6', shade: '#e0d3b8', line: '#c8b48a',
    blurb: 'Fast talker, faster feet.',
    seams: 'M28 40 Q50 48 72 40',
    tracks: ['ready', 'spin', 'recovery'],
  },
  {
    id: 'billiards', name: 'Eight', sport: 'Billiards', emoji: '\u{1F3B1}', comingSoon: true,
    image: '/companions-3d-billiards.png',
    title: 'The Cool Head',
    story: "Eight never rushes a shot and never misses the angle it saw three shots ago. Calm, dry humor, and the best geometry teacher you will ever have.",
    studies: "Fundamentals from the top pool players: stance, bridge, stroke straightness, cue ball control, aiming systems, speed control, pattern play and safety.",
    base: '#111318', shade: '#05060a', line: '#ffffff',
    blurb: 'Calm, dry, sees every angle.',
    seams: 'M50 36 a14 14 0 1 0 0.1 0',
    tracks: ['stance', 'stroke', 'speed'],
  },
];

export const byId = (id) => COMPANIONS.find((c) => c.id === id) || COMPANIONS[0];
